import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Input component provides a styled text input field with consistent styling
 * and accessibility features. Extends all standard HTML input element props.
 * 
 * @component
 * @param {React.ComponentProps<"input">} props - All standard HTML input element props
 * @returns {JSX.Element} A styled input element
 * 
 * @example
 * ```tsx
 * <Input type="text" placeholder="Enter your name" />
 * ```
 * 
 * @example
 * ```tsx
 * <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
 * ```
 */
const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
